import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Activity, RefreshCw, Database, AlertTriangle, CheckCircle2, Clock,
  Webhook, Search, FlaskConical, ServerCog, Inbox, Zap, ListTree, Building2,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const fmtMs = (n) => (n == null ? '—' : `${Math.round(n)} ms`);
const fmtDate = (s) => (!s ? '—' : new Date(s).toLocaleString());

const Card = ({ title, icon: Icon, children, action, testid }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900"
       data-testid={testid}>
    <div className="mb-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-red-600" />}
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const Pill = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
    ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
  }`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />} {label}
  </span>
);

const KV = ({ k, v, mono }) => (
  <div className="flex items-center justify-between border-b border-gray-100 py-1.5 text-xs last:border-none dark:border-gray-800">
    <span className="text-gray-500 dark:text-gray-400">{k}</span>
    <span className={`text-gray-900 dark:text-gray-100 ${mono ? 'font-mono' : ''}`}>{v}</span>
  </div>
);

const HealthPanel = ({ data }) => {
  const h = data?.health || {};
  const c = data?.content || {};
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <Card title="API Health" icon={Activity} testid="rh-card-health">
        <div className="mb-2">
          <Pill ok={!!h.ok} label={h.ok ? 'Healthy' : `Down (${h.code || 'unknown'})`} />
        </div>
        <KV k="Configured" v={h.configured ? 'Yes' : 'No'} />
        <KV k="HTTP Status" v={h.http_status ?? '—'} mono />
        <KV k="Latency" v={fmtMs(h.latency_ms)} mono />
        <KV k="Last check" v={fmtDate(h.checked_at)} />
      </Card>

      <Card title="Content Sync" icon={Database} testid="rh-card-content">
        <KV k="Total hotels cached" v={c.total_hotels ?? 0} mono />
        <KV k="Last sync" v={fmtDate(c.last_sync?.started_at)} />
        <KV k="Last sync mode" v={c.last_sync?.mode || '—'} />
        <KV k="Hotels in last run" v={c.last_sync?.upserted ?? '—'} mono />
        <KV k="Errors in last run" v={c.last_sync?.errors ?? '—'} mono />
      </Card>

      <Card title="Bookings Pipeline" icon={Inbox} testid="rh-card-bookings">
        {Object.entries(data?.bookings_by_status || {}).length === 0 ? (
          <p className="text-xs text-gray-400">No bookings yet.</p>
        ) : (
          Object.entries(data?.bookings_by_status || {}).map(([s, n]) => (
            <KV key={s} k={s} v={n} mono />
          ))
        )}
      </Card>

      <Card title="By country" icon={ListTree} testid="rh-card-country">
        {(c.by_country || []).length === 0 ? (
          <p className="text-xs text-gray-400">No content synced yet.</p>
        ) : (
          c.by_country.map((row) => (
            <KV key={row.country || 'unknown'} k={(row.country || 'unknown').toUpperCase()} v={row.count} mono />
          ))
        )}
      </Card>

      <Card title="Endpoint Status" icon={ServerCog} testid="rh-card-endpoints" >
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="font-medium">Endpoint</th>
              <th className="text-right font-medium">Calls</th>
              <th className="text-right font-medium">Err</th>
              <th className="text-right font-medium">Avg ms</th>
            </tr>
          </thead>
          <tbody>
            {(data?.api_by_endpoint || []).slice(0, 8).map((row) => (
              <tr key={row.endpoint} className="border-t border-gray-100 dark:border-gray-800">
                <td className="truncate py-1.5 pr-2 font-mono text-[10px]" title={row.endpoint}>
                  {row.endpoint.replace('/api/b2b/v3/', '')}
                </td>
                <td className="py-1.5 text-right font-mono">{row.calls}</td>
                <td className={`py-1.5 text-right font-mono ${row.errors > 0 ? 'text-rose-600' : ''}`}>{row.errors}</td>
                <td className="py-1.5 text-right font-mono">{Math.round(row.avg_latency_ms)}</td>
              </tr>
            ))}
            {(data?.api_by_endpoint || []).length === 0 && (
              <tr><td colSpan={4} className="py-2 text-gray-400">No API calls yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Card title="Last Webhook" icon={Webhook} testid="rh-card-webhook">
        {!data?.last_webhook ? (
          <p className="text-xs text-gray-400">No events received yet.</p>
        ) : (
          <>
            <KV k="Type" v={data.last_webhook.type || '—'} />
            <KV k="Status" v={data.last_webhook.status || '—'} />
            <KV k="Order ID" v={data.last_webhook.partner_order_id || '—'} mono />
            <KV k="Received" v={fmtDate(data.last_webhook.received_at)} />
          </>
        )}
      </Card>
    </div>
  );
};

const SyncPanel = ({ token, onRefresh }) => {
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/ratehawk/content/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(r.data.items || []);
    } catch (e) { /* ignored */ }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const trigger = async (kind) => {
    setBusy(kind);
    try {
      await axios.post(`${API}/api/admin/ratehawk/content/sync/${kind}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
      onRefresh && onRefresh();
    } finally { setBusy(null); }
  };

  return (
    <Card title="Sync History" icon={RefreshCw} testid="rh-card-sync-history" action={
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={!!busy} onClick={() => trigger('incremental')}
                data-testid="rh-sync-incremental">
          {busy === 'incremental' ? 'Syncing…' : 'Incremental'}
        </Button>
        <Button size="sm" disabled={!!busy} onClick={() => trigger('full')} data-testid="rh-sync-full">
          {busy === 'full' ? 'Syncing…' : 'Full Sync'}
        </Button>
      </div>
    }>
      {history.length === 0 ? (
        <p className="text-xs text-gray-400">No sync runs yet.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="font-medium">Started</th>
              <th className="font-medium">Mode</th>
              <th className="text-right font-medium">Considered</th>
              <th className="text-right font-medium">Upserted</th>
              <th className="text-right font-medium">Errors</th>
            </tr>
          </thead>
          <tbody>
            {history.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800">
                <td className="py-1.5">{fmtDate(r.started_at)}</td>
                <td className="py-1.5 capitalize">{r.mode}</td>
                <td className="py-1.5 text-right font-mono">{r.considered ?? 0}</td>
                <td className="py-1.5 text-right font-mono">{r.upserted ?? 0}</td>
                <td className={`py-1.5 text-right font-mono ${(r.errors || 0) > 0 ? 'text-rose-600' : ''}`}>{r.errors ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
};

const LogsPanel = ({ token, kind }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const load = useCallback(async () => {
    try {
      let url = `${API}/api/admin/ratehawk/${kind}?limit=100`;
      if (kind === 'api-logs' && onlyErrors) url += '&only_errors=true';
      const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.data.items || []);
    } catch (e) { /* ignored */ }
  }, [token, kind, onlyErrors]);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((it) => {
    if (!filter) return true;
    return JSON.stringify(it).toLowerCase().includes(filter.toLowerCase());
  });

  return (
    <Card
      title={kind === 'api-logs' ? 'API Logs' : kind === 'webhooks' ? 'Webhook Events' : kind === 'search-logs' ? 'Search Statistics' : kind}
      icon={kind === 'webhooks' ? Webhook : kind === 'search-logs' ? Search : ServerCog}
      testid={`rh-card-${kind}`}
      action={
        <div className="flex items-center gap-2">
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…"
                 className="rounded border border-gray-200 px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-800"
                 data-testid={`rh-filter-${kind}`} />
          {kind === 'api-logs' && (
            <label className="flex items-center gap-1 text-xs text-gray-500">
              <input type="checkbox" checked={onlyErrors} onChange={(e) => setOnlyErrors(e.target.checked)}
                     data-testid="rh-only-errors" />
              Errors only
            </label>
          )}
          <Button size="sm" variant="ghost" onClick={load} data-testid={`rh-refresh-${kind}`}>
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
      }
    >
      {filtered.length === 0 ? (
        <p className="text-xs text-gray-400">No entries.</p>
      ) : (
        <div className="max-h-[480px] space-y-1 overflow-auto">
          {filtered.map((it, idx) => (
            <details key={idx} className="rounded border border-gray-100 px-2 py-1 text-xs dark:border-gray-800">
              <summary className="cursor-pointer">
                <span className="font-mono">{fmtDate(it.timestamp || it.received_at)}</span>
                {it.endpoint && <span className="ml-2 font-mono text-[10px] text-gray-500">{it.endpoint}</span>}
                {it.partner_order_id && <span className="ml-2 font-mono text-[10px]">{it.partner_order_id}</span>}
                {it.status && <span className="ml-2 capitalize">{it.status}</span>}
                {typeof it.ok === 'boolean' && (
                  <span className={`ml-2 rounded px-1 text-[10px] ${it.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                    {it.ok ? 'ok' : (it.error || 'err')}
                  </span>
                )}
                {it.latency_ms != null && <span className="ml-2 text-gray-500">{Math.round(it.latency_ms)}ms</span>}
              </summary>
              <pre className="mt-1 max-h-72 overflow-auto rounded bg-gray-50 p-2 text-[10px] dark:bg-gray-950">
                {JSON.stringify(it, null, 2)}
              </pre>
            </details>
          ))}
        </div>
      )}
    </Card>
  );
};

const CertPanel = ({ token, onRefresh }) => {
  const [scenarios, setScenarios] = useState([]);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(null);
  const reload = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/api/admin/ratehawk/cert/scenarios`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/admin/ratehawk/cert/runs`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setScenarios(s.data.scenarios || []);
      setRuns(r.data.items || []);
    } catch (e) { /* ignored */ }
  }, [token]);
  useEffect(() => { reload(); }, [reload]);

  const runScenario = async (name) => {
    setBusy(name);
    try {
      await axios.post(`${API}/api/admin/ratehawk/cert/run/${name}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await reload();
      onRefresh && onRefresh();
    } finally { setBusy(null); }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card title="Cert Scenarios (ETG sandbox)" icon={FlaskConical} testid="rh-card-cert-scenarios">
        <div className="space-y-2">
          {scenarios.map((s) => (
            <div key={s.name} className="rounded-lg border border-gray-100 p-2 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold capitalize text-gray-900 dark:text-gray-100">
                  {s.name.replace(/_/g, ' ')}
                </span>
                <Button size="sm" variant="outline" disabled={busy === s.name}
                        onClick={() => runScenario(s.name)} data-testid={`rh-cert-run-${s.name}`}>
                  {busy === s.name ? 'Running…' : 'Run'}
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-gray-500">{s.description}</p>
              <p className="mt-0.5 text-[10px] uppercase tracking-wider text-gray-400">
                Expected: <span className="font-mono">{s.expected_final}</span>
              </p>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Recent Cert Runs" icon={Zap} testid="rh-card-cert-runs" action={
        <Button size="sm" variant="ghost" onClick={reload}><RefreshCw className="h-3 w-3" /></Button>
      }>
        {runs.length === 0 ? (
          <p className="text-xs text-gray-400">No runs yet.</p>
        ) : (
          <div className="max-h-[480px] space-y-1 overflow-auto">
            {runs.map((r) => (
              <details key={r.id} className="rounded border border-gray-100 px-2 py-1 text-xs dark:border-gray-800">
                <summary className="cursor-pointer">
                  <span className={`mr-2 inline-block rounded px-1.5 text-[10px] font-semibold ${
                    r.verdict === 'passed' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                  }`}>{r.verdict?.toUpperCase()}</span>
                  <span className="font-mono">{r.scenario}</span>
                  <span className="ml-2 text-gray-500">{fmtDate(r.started_at)}</span>
                </summary>
                <pre className="mt-1 max-h-72 overflow-auto rounded bg-gray-50 p-2 text-[10px] dark:bg-gray-950">
                  {JSON.stringify(r, null, 2)}
                </pre>
              </details>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

const LiveInventoryPanel = ({ token }) => {
  const [city, setCity] = useState('Dubai');
  const [checkIn, setCheckIn] = useState(inDays(30));
  const [checkOut, setCheckOut] = useState(inDays(32));
  const [adults, setAdults] = useState(2);
  const [residency, setResidency] = useState('gb');
  const [currency, setCurrency] = useState('USD');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRows([]);
    setMeta(null);
    try {
      const q = new URLSearchParams({
        city,
        check_in: checkIn,
        check_out: checkOut,
        adults: String(adults),
        residency,
        currency,
        limit: '500',
      });
      const r = await axios.get(`${API}/api/admin/ratehawk/inventory/live?${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.data?.ok) {
        setRows(r.data.hotels || []);
        setMeta({
          region_id: r.data.region_id,
          count: r.data.count,
          total_returned_by_etg: r.data.total_returned_by_etg,
        });
      } else {
        setError(r.data?.error || 'no_result');
        setMeta({ region_id: r.data?.region_id, count: 0 });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'request_failed');
    } finally {
      setLoading(false);
    }
  }, [city, checkIn, checkOut, adults, residency, currency, token]);

  return (
    <Card
      title="Live ETG Inventory"
      icon={Building2}
      testid="rh-card-live-inventory"
      action={
        <Button size="sm" onClick={run} disabled={loading} data-testid="rh-inventory-run">
          {loading ? <Loader loading /> : <><Search className="mr-1 h-3 w-3" />Search</>}
        </Button>
      }
    >
      <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-6">
        <div>
          <label className="text-[10px] uppercase text-gray-500">City</label>
          <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="rh-inventory-city"
                 placeholder="Dubai / Paris / Los Angeles" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-gray-500">Check-in</label>
          <Input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
                 data-testid="rh-inventory-checkin" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-gray-500">Check-out</label>
          <Input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)}
                 data-testid="rh-inventory-checkout" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-gray-500">Adults</label>
          <Input type="number" min={1} max={6} value={adults}
                 onChange={(e) => setAdults(parseInt(e.target.value, 10) || 1)}
                 data-testid="rh-inventory-adults" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-gray-500">Residency</label>
          <Input value={residency} onChange={(e) => setResidency(e.target.value.toLowerCase())}
                 data-testid="rh-inventory-residency" placeholder="gb / in / us" className="h-8 text-xs" />
        </div>
        <div>
          <label className="text-[10px] uppercase text-gray-500">Currency</label>
          <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                 data-testid="rh-inventory-currency" placeholder="USD / EUR" className="h-8 text-xs" />
        </div>
      </div>

      {error && (
        <div className="mb-3 rounded border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
          Error: {error}. Sandbox tip: Dubai / Paris / Los Angeles / Moscow return live inventory. Currency must be USD or EUR.
        </div>
      )}

      {meta && (
        <div className="mb-2 flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
          <span>region_id: <span className="font-mono text-gray-800 dark:text-gray-200">{meta.region_id ?? '—'}</span></span>
          <span>ETG returned: <span className="font-mono text-gray-800 dark:text-gray-200">{meta.total_returned_by_etg ?? 0}</span></span>
          <span>Rows shown: <span className="font-mono text-gray-800 dark:text-gray-200">{meta.count ?? 0}</span></span>
        </div>
      )}

      <div className="max-h-[560px] overflow-auto rounded border border-gray-100 dark:border-gray-800">
        <table className="w-full text-xs" data-testid="rh-inventory-table">
          <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
            <tr className="text-left text-gray-500">
              <th className="px-2 py-1.5 font-medium">Hotel Name</th>
              <th className="px-2 py-1.5 font-medium">HID</th>
              <th className="px-2 py-1.5 font-medium">City</th>
              <th className="px-2 py-1.5 font-medium">Country</th>
              <th className="px-2 py-1.5 text-right font-medium">Star</th>
              <th className="px-2 py-1.5 text-right font-medium">Rooms</th>
              <th className="px-2 py-1.5 text-right font-medium">Rates</th>
              <th className="px-2 py-1.5 text-right font-medium">Images</th>
              <th className="px-2 py-1.5 font-medium">Kind</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={9} className="px-2 py-4 text-center text-gray-400">
                No live inventory loaded. Enter destination + dates and click Search.
              </td></tr>
            )}
            {loading && (
              <tr><td colSpan={9} className="px-2 py-4 text-center text-gray-400">
                Querying ETG sandbox…
              </td></tr>
            )}
            {rows.map((h) => (
              <tr key={h.hid} className="border-t border-gray-100 dark:border-gray-800"
                  data-testid={`rh-inventory-row-${h.hid}`}>
                <td className="px-2 py-1.5">{h.name || '—'}</td>
                <td className="px-2 py-1.5 font-mono text-[10px] text-gray-600">{h.hid}</td>
                <td className="px-2 py-1.5">{h.city || '—'}</td>
                <td className="px-2 py-1.5">{h.country || h.country_code || '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono">{h.star_rating || 0}</td>
                <td className="px-2 py-1.5 text-right font-mono">{h.available_rooms ?? '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono">{h.rates_count ?? '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono">{h.images_count ?? 0}</td>
                <td className="px-2 py-1.5 text-gray-500">{h.kind || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const Loader = () => <RefreshCw className="h-3 w-3 animate-spin" />;


const BookingsPanel = ({ token }) => {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/ratehawk/bookings?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(r.data.items || []);
    } catch (e) { /* ignored */ }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card title="Recent RateHawk Bookings" icon={Inbox} testid="rh-card-recent-bookings" action={
      <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="h-3 w-3" /></Button>
    }>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400">No bookings yet.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="font-medium">Order ID</th>
              <th className="font-medium">Status</th>
              <th className="font-medium">Created</th>
              <th className="font-medium">Updated</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.partner_order_id} className="border-t border-gray-100 dark:border-gray-800">
                <td className="py-1.5 font-mono text-[10px]">{b.partner_order_id}</td>
                <td className="py-1.5">
                  <Badge variant="outline" className="capitalize">{b.status || '—'}</Badge>
                </td>
                <td className="py-1.5">{fmtDate(b.created_at)}</td>
                <td className="py-1.5">{fmtDate(b.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
};

const AdminRateHawk = () => {
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('elite_admin_token') : null;
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDiag = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/ratehawk/diagnostics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDiag(r.data);
    } catch (e) {
      setDiag(null);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadDiag(); }, [loadDiag]);

  return (
    <div className="space-y-4" data-testid="admin-ratehawk-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            RateHawk (ETG API V3) Dashboard
          </h1>
          <p className="text-sm text-gray-500">
            Live diagnostics, content sync, certification scenarios, booking pipeline.
          </p>
        </div>
        <Button onClick={loadDiag} disabled={loading} variant="outline" data-testid="rh-refresh-diag">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 md:grid-cols-7">
          <TabsTrigger value="overview" data-testid="rh-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="live" data-testid="rh-tab-live">Live Inventory</TabsTrigger>
          <TabsTrigger value="sync" data-testid="rh-tab-sync">Sync</TabsTrigger>
          <TabsTrigger value="cert" data-testid="rh-tab-cert">Certification</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="rh-tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="logs" data-testid="rh-tab-logs">API Logs</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="rh-tab-webhooks">Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 pt-4">
          {!diag ? <p className="text-sm text-gray-500">Loading…</p> : <HealthPanel data={diag} />}
        </TabsContent>

        <TabsContent value="live" className="space-y-4 pt-4">
          <LiveInventoryPanel token={token} />
        </TabsContent>

        <TabsContent value="sync" className="space-y-4 pt-4">
          <SyncPanel token={token} onRefresh={loadDiag} />
        </TabsContent>

        <TabsContent value="cert" className="space-y-4 pt-4">
          <CertPanel token={token} onRefresh={loadDiag} />
        </TabsContent>

        <TabsContent value="bookings" className="space-y-4 pt-4">
          <BookingsPanel token={token} />
        </TabsContent>

        <TabsContent value="logs" className="space-y-4 pt-4">
          <LogsPanel token={token} kind="api-logs" />
          <LogsPanel token={token} kind="search-logs" />
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4 pt-4">
          <LogsPanel token={token} kind="webhooks" />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminRateHawk;
